import { ChangeDetectorRef, DestroyRef, Injectable, OnInit, inject, output } from '@angular/core';
import { Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { DropdownModel, Filter } from '../../models';
import { ErrorHandlingService } from '../../services';
import { FormGroup } from '@angular/forms';
import { filterEntities } from '../../../functions';
import { Router } from '@angular/router';
import { ListFilterState } from './list-filter.state.util';

@Injectable({
  providedIn: 'root',
})
export abstract class ListFilterComponent implements OnInit {
  destroyRef = inject(DestroyRef);
  errorHandlingService = inject(ErrorHandlingService);
  refresh = output<Filter[]>() as any;
  showMoreFilters: boolean = false;
  showClearButton: boolean = false;
  showMoreFiltersButton: boolean = false;
  private router = inject(Router);
  changeDetectorRef = inject(ChangeDetectorRef);

  private lastFiltersHash: string | null = null;

  constructor(public filterFormGroup: FormGroup) {}

  protected get storageKey(): string {
    const routePath = this.router.url?.split('?')[0] ?? '';
    return ListFilterState.buildKey(routePath, this.constructor.name);
  }

  ngOnInit(): void {
    let restoredOnce = false;

    const restored = ListFilterState.connect(this.storageKey, this.destroyRef, {
      debounceMs: 300,
      onRestore: (values: any) => {
        restoredOnce = true;
        this.showClearButton = !this.isFormEmpty(values);

        const rawValue = JSON.parse(JSON.stringify(values));

        this.loadSuggestions(rawValue).then(() => {
          this.changeDetectorRef.detectChanges();
          this.filterFormGroup.patchValue(rawValue);
          this.changeDetectorRef.markForCheck();
          this.emitFiltersIfChanged();
        });
      },
    });

    if (!restoredOnce && !restored) {
      this.emitFiltersIfChanged();
    }

    this.filterFormGroup.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.showClearButton = !this.isFormEmpty(this.filterFormGroup.value);
    });
  }

  public applyFilters() {
    this.emitFiltersIfChanged();
    const value = this.filterFormGroup.getRawValue?.() ?? this.filterFormGroup.value;
    ListFilterState.snapshot(this.storageKey, value);
  }

  public showAdvancedFilters() {
    this.showMoreFilters = !this.showMoreFilters;
  }

  public onShowMoreFiltersChange($event: boolean) {
    this.showMoreFilters = $event;
  }

  public async filterEntities(
    service: { suggest: (query: string) => Observable<any>; [key: string]: any },
    setFilteredData: (result: any) => void,
    event: AutoCompleteCompleteEvent,
    isValue?: boolean,
    method: string = 'suggest',
  ) {
    return filterEntities(service, setFilteredData, event, isValue, method, undefined, this.errorHandlingService);
  }

  public buildFilters(): Filter[] {
    return [];
  }

  public onFilterChange() {
    const filters = this.buildFilters().map((filter) => ({
      ...filter,
      condition: 'AND',
    }));
    this.refresh.emit(<Filter[]>filters);
  }

  public clearAllFilters() {
    this.filterFormGroup.reset();
    this.lastFiltersHash = null;
    ListFilterState.clear(this.storageKey);
    this.applyFilters();
  }

  private emitFiltersIfChanged(): void {
    const filters = this.buildFilters().map((f) => ({ ...f, condition: 'AND' }));
    const hash = JSON.stringify(filters);
    if (hash !== this.lastFiltersHash) {
      this.lastFiltersHash = hash;
      this.refresh.emit(<Filter[]>filters);
    }
  }

  private isFormEmpty(values: any): boolean {
    return Object.values(values ?? {}).every(
      (value) =>
        value === null ||
        value === undefined ||
        value === '' ||
        value === false ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'boolean' && value === false),
    );
  }

  public async loadSuggestions(values: any) {}
}
